import React, { useEffect, useState } from "react";
import axiosInstance from "../../config/axios";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TableContainer,
  Typography,
} from "@mui/material";

type ClubRow = {
  id: number;
  name: string;
  football_division: string;
  league: string;
  profile_picture?: string;
};

export const ClubsAdminPage: React.FC = () => {
  const [clubs, setClubs] = useState<ClubRow[]>([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    level: "HIGH_SCHOOL",
    football_division: "A",
    league: "",
    conference: "",
    mascot: "",
    profile_picture: "",
    schedule_url: "",
    school_url: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
  });

  const fetchClubs = async () => {
    const response = await axiosInstance.get("/clubs/");
    setClubs(response.data);
  };

  useEffect(() => {
    fetchClubs().catch((err) => setError(err?.message ?? "Unable to fetch clubs"));
  }, []);

  const onCreate = async () => {
    try {
      await axiosInstance.post("/clubs/", form);
      setOpen(false);
      setMessage("Club created.");
      await fetchClubs();
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? "Unable to create club");
    }
  };

  const normalizedSearch = search.trim().toLowerCase();
  const filteredClubs = clubs.filter((club) =>
    !normalizedSearch ||
    club.name.toLowerCase().includes(normalizedSearch) ||
    club.football_division.toLowerCase().includes(normalizedSearch)
  );

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h5">Clubs</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Add Club
        </Button>
      </Box>

      <TextField
        label="Search clubs"
        placeholder="Search by name or division"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      />

      <TableContainer sx={{ width: "100%", maxHeight: "calc(100vh - 220px)", overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Logo</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Division</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClubs.map((club) => (
              <TableRow key={club.id}>
                <TableCell sx={{ width: 64 }}>
                  {club.profile_picture ? (
                    <Avatar
                      variant="rounded"
                      src={club.profile_picture}
                      alt={`${club.name} logo`}
                      sx={{ width: 40, height: 40, bgcolor: "transparent" }}
                      imgProps={{ style: { objectFit: "contain" } }}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{club.name}</TableCell>
                <TableCell>{club.football_division}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Club</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 2 }}>
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField label="Level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} />
          <TextField label="Division" value={form.football_division} onChange={(e) => setForm({ ...form, football_division: e.target.value })} />
          <TextField label="League" value={form.league} onChange={(e) => setForm({ ...form, league: e.target.value })} />
          <TextField label="Conference" value={form.conference} onChange={(e) => setForm({ ...form, conference: e.target.value })} />
          <TextField label="Mascot" value={form.mascot} onChange={(e) => setForm({ ...form, mascot: e.target.value })} />
          <TextField label="Logo Path (/assets/logos/...)" value={form.profile_picture} onChange={(e) => setForm({ ...form, profile_picture: e.target.value })} />
          <TextField label="Schedule URL" value={form.schedule_url} onChange={(e) => setForm({ ...form, schedule_url: e.target.value })} />
          <TextField label="School URL" value={form.school_url} onChange={(e) => setForm({ ...form, school_url: e.target.value })} />
          <TextField label="Street Address" value={form.street_address} onChange={(e) => setForm({ ...form, street_address: e.target.value })} />
          <TextField label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <TextField label="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
          <TextField label="Zip Code" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
          <TextField label="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onCreate} disabled={!form.name || !form.league || !form.street_address || !form.city || !form.state || !form.zip_code || !form.country}>
            Create Club
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(message)} autoHideDuration={4000} onClose={() => setMessage(null)}>
        <Alert severity="success" onClose={() => setMessage(null)}>
          {message}
        </Alert>
      </Snackbar>
      <Snackbar open={Boolean(error)} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
